import {useStoreon} from "@storeon/solidjs";
import {createEffect, createMemo, createSignal, Switch, Match, For, Show} from "solid-js";
import {Link, useParams} from "@solidjs/router";
import {Button, Modal} from "solid-bootstrap";
import reservationStatus from "../../../helpers/reservationStatus";
import {formatDate, formatHoraire, describe, cancelErrorMessage} from "../../../helpers/reservationFormat";

/**
 * /order/cancel/<code> : annulation réservation par réservation, SANS session.
 *
 * Le code de commande est le justificatif. Cette page sert les e-mails de
 * confirmation DÉJÀ ENVOYÉS, dont le bouton pointe ici : les nouveaux portent
 * désormais un lien magique vers /user/orders. Ces anciens liens circuleront
 * des mois. La plupart des clients réservent en invité et n'ont jamais
 * défini de mot de passe : exiger une connexion ici les bloquerait purement et
 * simplement. Le visiteur ne porte aucun jeton d'institution (index.js exempte
 * l'étape "cancel" du garde-fou sur state.token) et la requête part sur le
 * canal par défaut, auquel les commandes appartiennent également.
 *
 * Le backend fait autorité : il vérifie que la ligne appartient bien à la
 * commande que le code désigne, calcule "cancelable" et refuse tout le reste.
 */
export default props => {
    const [state, dispatch] = useStoreon();
    const params = useParams();

    // createEffect et non un simple appel : le routeur réutilise le composant
    // quand seul le paramètre change, or params.orderCode est réactif dans le
    // rendu. Sans effet, passer d'un lien à un autre affiche le NOUVEAU numéro
    // de commande au-dessus des réservations de l'ANCIENNE.
    createEffect(() => dispatch("loadOrderReservations", params.orderCode));
    // cancelReservationResult vit dans le store global : sans ce nettoyage, une
    // annulation échouée laisse sa bannière rouge affichée au retour sur la page.
    dispatch("clearCancelReservationResult");

    // Réservation dont l'annulation est en cours de confirmation (null = pas de dialogue).
    const [pending, setPending] = createSignal(null);

    // Le backend trie déjà (date de créneau croissante) : ne pas retrier ici.
    // orderReservations vaut null à l'init et sur erreur : on ne suppose pas un tableau.
    const rows = createMemo(() => Array.isArray(state.orderReservations) ? state.orderReservations : []);

    // Toutes les réservations de la commande sont annulées : on le dit, et on
    // offre une sortie plutôt qu'un cul-de-sac.
    const allCancelled = createMemo(() => rows().length > 0 && rows().every((row) => row.cancelled));

    const isLoading = () => state.loading.orderReservations;

    const askCancel = (reservation) => {
        dispatch("clearCancelReservationResult");
        setPending(reservation);
    };

    const dismiss = () => setPending(null);

    // Rien n'est envoyé tant que le client n'a pas confirmé.
    // dispatch() de storeon ne renvoie pas de promesse : inutile de l'attendre,
    // le rechargement de la liste est piloté par le store.
    const confirmCancel = () => {
        const reservation = pending();
        if (!reservation) {
            return;
        }
        setPending(null);
        dispatch("cancelReservationByCode", {
            code: params.orderCode,
            orderLineId: reservation.orderLineId
        });
    };

    const cancelError = () => cancelErrorMessage(state.cancelReservationResult);

    return (
        <div class="reservations">
            <h2 class="mb-4">Annuler une réservation</h2>
            <Show when={cancelError()}>
                <p class="text-danger" role="alert">{cancelError()}</p>
            </Show>
            <Switch>
                <Match when={isLoading()}>
                    <p>Chargement de vos réservations…</p>
                </Match>
                {/*
                  Le lien cassé est l'échec le PLUS COURANT en vrai : les
                  clients mail coupent les URL longues. Un message générique
                  laisserait le client croire que sa commande a disparu.
                */}
                <Match when={state.orderReservationsUnknownCode}>
                    <p class="text-danger" role="alert">
                        Nous n'avons pas trouvé de commande correspondant à ce lien.
                    </p>
                    <p>
                        Le lien de votre e-mail a peut-être été coupé par votre messagerie&nbsp;:
                        vérifiez qu'il est complet, ou copiez-le entièrement dans la barre d'adresse.
                    </p>
                    <p class="text-muted small">
                        Le problème persiste&nbsp;? Contactez votre établissement en indiquant
                        votre numéro de commande, ou retrouvez vos réservations depuis
                        <Link href="/user/orders"> Mes réservations</Link>.
                    </p>
                </Match>
                <Match when={state.orderReservationsError}>
                    <p class="text-danger" role="alert">Une erreur est survenue lors du chargement de vos réservations. Veuillez réessayer plus tard.</p>
                </Match>
                <Match when={rows().length === 0}>
                    <p>Cette commande ne contient aucune réservation.</p>
                </Match>
                <Match when={rows().length > 0}>
                    <p class="text-muted">Commande n° {params.orderCode}</p>
                    <Show
                        when={!allCancelled()}
                        fallback={
                            <p class="mb-4">
                                Toutes les réservations de cette commande sont annulées.
                                Vous pouvez <Link href="/">réserver à nouveau</Link> quand vous le souhaitez.
                            </p>
                        }
                    >
                        {/*
                          Le point qui a motivé la suppression de l'ancienne
                          page : elle annulait TOUTE la commande d'un clic. On
                          le dit explicitement pour lever le doute.
                        */}
                        <p class="mb-4">
                            Sélectionnez la réservation à annuler&nbsp;:
                            <strong> seule celle-ci sera annulée</strong>, les autres sont conservées.
                        </p>
                    </Show>
                    <ul class="list-unstyled">
                        <For each={rows()}>
                            {(row) => {
                                const status = reservationStatus(row);
                                const horaire = formatHoraire(row);
                                const activityName = row.activityName || "Activité";
                                const assetSource = row.featuredAsset && row.featuredAsset.source;
                                return (
                                    <li class="d-flex align-items-center border-top py-3">
                                        <div
                                            class="me-3 rounded flex-shrink-0"
                                            /* URL entre quotes : sans elles, une source contenant ")" ou ";"
                                               sortirait du url() et injecterait des déclarations CSS. */
                                            style={`width: 64px; height: 64px; background-repeat: no-repeat; background-position: center; background-image: url("${assetSource || "/images/logo_ccpl.jpg"}"); background-size: ${assetSource ? "cover" : "90%"}`}
                                        ></div>
                                        <div class="flex-fill">
                                            <div class="fw-bold">{activityName}</div>
                                            <div>{formatDate(row)}{horaire ? " · " + horaire : ""}</div>
                                            <div>{row.quantity} place{row.quantity > 1 ? "s" : ""}</div>
                                        </div>
                                        <span
                                            class={"badge bg-" + status.variant + (status.variant === "warning" ? " text-dark" : "")}
                                            aria-label={status.ariaLabel}
                                        >{status.label}</span>
                                        <Show when={row.cancelable}>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                class="ms-3"
                                                disabled={!!state.cancellingReservation}
                                                aria-label={"Annuler la réservation : " + describe(row)}
                                                onClick={() => askCancel(row)}
                                            >Annuler</Button>
                                        </Show>
                                    </li>
                                );
                            }}
                        </For>
                    </ul>
                </Match>
            </Switch>
            <Modal
                show={!!pending()}
                onHide={dismiss}
                centered
                backdrop="static"
                aria-labelledby="cancel-reservation-title"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="cancel-reservation-title">Annuler cette réservation ?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Show when={pending()}>
                        <p class="fw-bold mb-1">{describe(pending())}</p>
                    </Show>
                    <p class="mb-1">Seule cette réservation sera annulée ; les autres réservations de la commande sont conservées.</p>
                    <p class="mb-0">Cette action est définitive : la réservation ne pourra pas être rétablie.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={dismiss}>Conserver</Button>
                    <Button variant="danger" onClick={confirmCancel}>Annuler la réservation</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};
